// ADR CTV-732: typed wrappers over the super-admin Publica upload
// worklist. Every route is POST + super_admin only (see
// selfservice-api/src/modules/publica-uploads/publica-uploads.controller.ts).

import { apiClient } from '../lib/apiClient';

export interface PublicaUploadRow {
  creativeId: string;
  filename: string;
  /** What Publica will call the file once it lands in the library. */
  normalizedFilename: string;
  storagePath: string | null;
  creativeStatus: string;
  /** `miss` = marked (or pushed) but the library scrape couldn't find it. */
  rowKind: 'open' | 'miss';
  publicaUploadedAt: string | null;
  publicaLibraryMissAt: string | null;
  submissionId: string;
  submissionStatus: string;
  campaignName: string | null;
  advertiserName: string | null;
  startDate: string | null;
  endDate: string | null;
  stationId: string | null;
  stationName: string | null;
  stationSlug: string | null;
  organizationId: string | null;
  organizationName: string | null;
  publicaPublisherId: string | null;
  /** The submission is captured: a paid campaign is waiting on this file. */
  blocking: boolean;
}

export interface PublicaUploadReleaseOutcome {
  released: boolean;
  createEnqueued: boolean;
  swapJobsEnqueued: number;
  libraryMissJobsReset: number;
}

export interface MarkUploadedResult {
  creativeId: string;
  submissionId: string;
  publicaUploaded: boolean;
  release: PublicaUploadReleaseOutcome;
}

export function listPublicaUploads(filters: {
  organizationId?: string;
  stationId?: string;
  submissionId?: string;
} = {}): Promise<{ rows: PublicaUploadRow[] }> {
  return apiClient.post<{ rows: PublicaUploadRow[] }>('/publica-uploads/list', filters);
}

export function getPublicaUploadsCount(): Promise<{ count: number; blockingCount: number }> {
  return apiClient.post<{ count: number; blockingCount: number }>('/publica-uploads/count', {});
}

export function markPublicaUploaded(creativeId: string): Promise<MarkUploadedResult> {
  return apiClient.post<MarkUploadedResult>(
    `/publica-uploads/creatives/${creativeId}/mark-uploaded`,
    {},
  );
}

export function unmarkPublicaUploaded(
  creativeId: string,
): Promise<{ creativeId: string; submissionId: string; publicaUploaded: boolean }> {
  return apiClient.post(`/publica-uploads/creatives/${creativeId}/unmark`, {});
}

/**
 * Signed download URL for one creative. Reuses the existing creatives
 * endpoint, which already short-circuits its access check for super_admin.
 * The worklist itself returns no signed URLs — they're minted on demand, one
 * per download, so a long-lived worklist page never holds a pile of live
 * storage links.
 */
export function getCreativeViewUrl(
  submissionId: string,
  creativeId: string,
): Promise<{ url: string }> {
  return apiClient.post<{ url: string }>('/creatives/view-url', {
    submissionId,
    creativeId,
  });
}
